/**
 * useObservationStageData — shared data hook for the four OBS appointment
 * phases (Task Administration → ADOS-informed coding → Criterion Mapping →
 * Review and Synthesis).
 *
 * Extracts the common Firestore data plumbing, state management, and write
 * helpers that were previously co-located in the legacy single-page
 * AdultAsdObservation component (no longer routed). Each phase page consumes
 * this hook instead of duplicating context access, state hydration, write
 * gating, and progress persistence logic.
 *
 * All clinical data reads from and writes to `assessmentStages/observation`
 * via ModularPatientDataContext's `updateAssessmentStage` (granular dot-path
 * updates, debounced batching, transactional Firestore writes).
 *
 * Write gating: only the clinician assigned to the OBS stage can write (via
 * `canEdit`). The ?preview=true query param forces read-only mode for
 * previewing by non-assigned clinicians or org admins.
 *
 * Deep-link guard: workflows that disable Behavioural Observation (e.g. LMP)
 * are redirected to the patient overview once the header has loaded.
 */

import { useState, useCallback, useMemo, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ModularPatientDataContext } from '../../../../context/ModularPatientDataContext';
import { useIsPreviewMode } from '../../../../hooks/useIsPreviewMode';
import { useCanEditStage } from '../../../../hooks/useCanEditStage';
import { usePreviewAwareNavigate } from '../../../../hooks/usePreviewAwareNavigate';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import { useVideoStream } from '../../../../context/VideoStreamContext';
import { ADULT_ASD_OBSERVATION_TASKS } from '../config/observationTasksConfig';
import { moveNoteBetweenArrays } from '../../../../utils/structuredNotesMove';
import { buildObsNoteMoveTargets } from '../utils/obsNoteMoveTargets';
import { normaliseNotesValue } from '../../../../utils/autismReviewData';
import { computeAutismOBSProgress } from '../../../../utils/autismObsRouting';
import { ASD_CONDITION_LABEL } from '../../../../constants/autismConditions';
import { SELF_REPORTED_TAG_ID } from '../../../../utils/obsSelfReportGeneration';

/**
 * Default task IDs pre-selected for Adult ASD assessments, in the order the
 * clinician works through them.
 *
 * Derived from the single source of truth (`ADULT_ASD_OBSERVATION_TASKS`)
 * rather than hand-maintained, so the preset can never silently drift from the
 * task config it is meant to mirror. The array order IS the assessment order.
 */
const DEFAULT_SELECTED_TASK_IDS = ADULT_ASD_OBSERVATION_TASKS.map((t) => t.id);

/**
 * Parse a global note-box id used by the cross-page "Move" picker into the
 * pieces needed to read/write Observation note arrays.
 *
 * Two shapes exist:
 *   - "task:<taskId>:masterNotes"  -> a task's master notes box
 *   - "general:<panelId>:notes"    -> a General Observations panel's notes box
 *
 * `dataKey` is the key under `allTaskData` (numeric task id, or "general_<panelId>")
 * and `field` is the note array field within that entry.
 */
function parseObsBoxId(id) {
  const parts = String(id).split(':');
  if (parts[0] === 'task' && parts[1] && parts[2]) {
    return { kind: 'task', dataKey: parts[1], field: parts[2] };
  }
  if (parts[0] === 'general' && parts[1] && parts[2]) {
    return { kind: 'general', dataKey: `general_${parts[1]}`, field: parts[2] };
  }
  return null;
}

/**
 * Provides all shared state, derived data, and write handlers for the OBS
 * module's four routed phase pages. Each page destructures what it needs.
 *
 * @param {object} [options]
 * @param {boolean} [options.excludeSensoryPanel=false]  Omit Sensory (B4) from the
 *   cross-page Move picker — Task Administration hides that sidebar item.
 * @returns {Object} A bag of state values, setters, handlers, and flags.
 */
export default function useObservationStageData({ excludeSensoryPanel = false } = {}) {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();
  const videoStream = useVideoStream();
  const tasks = ADULT_ASD_OBSERVATION_TASKS;

  // ── Context: Firestore-backed patient data ─────────────────────────────
  const {
    header,
    assessmentStages,
    reportContent,
    updateAssessmentStage,
    flushStageWrites,
    loadedPatientId,
  } = useContext(ModularPatientDataContext);

  // Deep-link guard for orgs that do not run Behavioural Observation (e.g. LMP).
  // Wait until the header has loaded for this patient so a transient null
  // config does not bounce TOC clinicians away.
  const { claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);
  const isObservationEnabled = wf?.isObservationEnabled !== false;
  // v2 = redesigned Behavioural Observation (tagged notes + criteria-level
  // evidence + Other Behaviours). Stamp-only; absent -> v1.
  const isObsV2 = wf?.isObsV2 === true;
  // Appointment Checklist: resolved via the three-tier stamp system in
  // useWorkflowConfig (stamp → legacy → disabled default).
  const isAppointmentChecklistEnabled = wf?.isAppointmentChecklistEnabled === true;
  useEffect(() => {
    if (loadedPatientId === patientId && !isObservationEnabled) {
      navigate(`/patient/${patientId}/adult-autism`, { replace: true });
    }
  }, [loadedPatientId, patientId, isObservationEnabled, navigate]);

  // ── Permission gating ──────────────────────────────────────────────────
  const permissionModel = header || {};
  const isPreviewMode = useIsPreviewMode();
  const canEdit = useCanEditStage(permissionModel, 'OBS', { previewMode: isPreviewMode });

  // Track whether we have already promoted status from notStarted -> inProgress
  const hasStartedRef = useRef(false);

  useEffect(() => {
    videoStream?.setVideoEnabled(true);
    return () => videoStream?.setVideoEnabled(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Initial state hydration from Firestore ─────────────────────────────
  const obsData = assessmentStages?.observation?.data;

  const [selectedTaskIds, setSelectedTaskIds] = useState(
    () => obsData?.selectedTaskIds ?? [...DEFAULT_SELECTED_TASK_IDS],
  );

  const [allTaskData, setAllTaskData] = useState(
    () => obsData?.allTaskData ?? {},
  );

  const [confidenceStyle, setConfidenceStyle] = useState(
    () => obsData?.confidenceStyle ?? 'ternary',
  );

  const [adosData, setAdosData] = useState(
    () => obsData?.adosData ?? {},
  );

  // Criteria-level evidence decisions (Criterion Mapping redesign): one Clear /
  // Partial / No / Inconclusive choice per DSM criterion (A1–B4), stored at
  // observation.data.criteriaEvidence rather than per-task.
  const [criteriaEvidence, setCriteriaEvidence] = useState(
    () => obsData?.criteriaEvidence ?? {},
  );

  // Optional free-text criterion notes written during Criterion Mapping —
  // bullets that are not already covered by tagged Task Admin / ADOS notes.
  // Stored at observation.data.criteriaNotes.{criterionId}.
  const [criteriaNotes, setCriteriaNotes] = useState(
    () => obsData?.criteriaNotes ?? {},
  );

  // ── Review state (persisted to Firestore at observation.review) ────────
  const obsReview = assessmentStages?.observation?.review;

  const [confidence, setConfidence] = useState(() => obsReview?.confidence ?? null);
  const [confidenceTernary, setConfidenceTernary] = useState(() => obsReview?.confidenceTernary ?? null);
  const [formulationConditions, setFormulationConditions] = useState(
    () => (obsReview?.formulationConditions || []).filter(c => c !== ASD_CONDITION_LABEL),
  );
  const [differentialsConfirmed, setDifferentialsConfirmed] = useState(() => obsReview?.differentialsConfirmed ?? false);
  const [emergingFormulation, setEmergingFormulation] = useState(() => normaliseNotesValue(obsReview?.emergingFormulation));
  const [confidentialNotes, setConfidentialNotes] = useState(() => normaliseNotesValue(obsReview?.formulationNotes));

  // ── One-time hydration from Firestore context ──────────────────────────
  const hasSeededRef = useRef(false);
  useEffect(() => {
    if (!obsData || hasSeededRef.current) return;
    hasSeededRef.current = true;

    if (obsData.selectedTaskIds) setSelectedTaskIds(obsData.selectedTaskIds);
    if (obsData.allTaskData) setAllTaskData(obsData.allTaskData);
    if (obsData.confidenceStyle) setConfidenceStyle(obsData.confidenceStyle);
    if (obsData.adosData) setAdosData(obsData.adosData);
    if (obsData.criteriaEvidence) setCriteriaEvidence(obsData.criteriaEvidence);
    if (obsData.criteriaNotes) setCriteriaNotes(obsData.criteriaNotes);

    const review = assessmentStages?.observation?.review;
    if (review) {
      if (review.confidence !== undefined) setConfidence(review.confidence);
      if (review.confidenceTernary !== undefined) setConfidenceTernary(review.confidenceTernary);
      setFormulationConditions((review.formulationConditions || []).filter(c => c !== ASD_CONDITION_LABEL));
      if (review.differentialsConfirmed !== undefined) setDifferentialsConfirmed(review.differentialsConfirmed);
      setEmergingFormulation(normaliseNotesValue(review.emergingFormulation));
      setConfidentialNotes(normaliseNotesValue(review.formulationNotes));
    }
  }, [obsData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Normalise selectedTaskIds into Firestore (one-off, non-destructive) ──
  // A clinician who accepts the default task preset never adds or removes a
  // task, so the add/remove handlers below (the only writers of
  // `data.selectedTaskIds`) never fire and the field is never persisted — the
  // module simply runs on the in-memory default seeded above. Every OTHER
  // reader of this stage reads the task list straight from Firestore: the MDT
  // Review evidence tables, the Overview resume-routing helpers, and the Python
  // LLM criteria extractor. With no persisted list they all see an empty
  // assessment — the extractor in particular then emits a "no evidence" line
  // for all seven DSM-5 criteria even though every task was written up. We
  // repair that here by persisting the preset the first time the stage loads
  // without one.
  //
  // Deliberate design choices:
  //   - Nullish check ONLY, never truthiness. An explicit [] is a clinician who
  //     removed every task; the UI shows them an empty assessment to match, so
  //     we must not silently re-add the 13 defaults behind their back.
  //   - This is a system-normalisation write, NOT clinician activity, so it
  //     does NOT go through maybePromoteStatus. A mere page load must never flip
  //     status notStarted -> inProgress, which gates the Overview progress pill
  //     and the Start/Continue call-to-action.
  //   - Runs at most once per mount, only when we can edit, and only after the
  //     stage document for THIS patient has loaded, so we never seed into a
  //     document whose real list has simply not arrived yet.
  const hasNormalisedTaskIdsRef = useRef(false);
  useEffect(() => {
    if (hasNormalisedTaskIdsRef.current) return;
    if (!canEdit) return;
    if (loadedPatientId !== patientId) return;
    const stage = assessmentStages?.observation;
    if (!stage) return;
    if (stage.data?.selectedTaskIds != null) return;

    hasNormalisedTaskIdsRef.current = true;
    updateAssessmentStage('observation', {
      'data.selectedTaskIds': [...DEFAULT_SELECTED_TASK_IDS],
    });
  }, [canEdit, loadedPatientId, patientId, assessmentStages?.observation, updateAssessmentStage]);

  // ── Status promotion ───────────────────────────────────────────────────
  const maybePromoteStatus = useCallback((dotPathUpdates) => {
    const currentStatus = assessmentStages?.observation?.status;
    if (!hasStartedRef.current && currentStatus === 'notStarted') {
      hasStartedRef.current = true;
      return { ...dotPathUpdates, status: 'inProgress' };
    }
    return dotPathUpdates;
  }, [assessmentStages?.observation?.status]);

  // ── Progress tracking ──────────────────────────────────────────────────
  /**
   * Persists the dashboard progress fraction for the whole OBS module.
   *
   * We deliberately write the full three-phase fraction (Task Administration
   * pro-rata + Criterion Mapping + Review) rather than just the task-notes
   * fraction. Writing notes-only would let `progress` hit 100% the moment the
   * last task was written up — and Overview turns a 100% module into a "Review"
   * button that jumps straight to Review and Synthesis, skipping Criterion
   * Mapping entirely. Keeping the stored value honest keeps that shortcut
   * honest too.
   *
   * The review third is passed explicitly because callers sit inside the task
   * data handlers, where the freshest confidence rating is local state rather
   * than the (debounced) Firestore copy.
   */
  const computeAndWriteProgress = useCallback((
    nextTaskData,
    nextSelectedIds,
    nextConfidenceTernary = confidenceTernary,
    nextCriteriaEvidence = criteriaEvidence,
  ) => {
    if (!canEdit) return;
    const status = assessmentStages?.observation?.status;
    // A completed stage always reports 1 from here, so late edits can never
    // regress a finished module's pill.
    const progress = computeAutismOBSProgress({
      data: {
        selectedTaskIds: nextSelectedIds,
        allTaskData: nextTaskData,
        criteriaEvidence: nextCriteriaEvidence,
      },
      review: { confidenceTernary: nextConfidenceTernary },
      status,
    });
    updateAssessmentStage('observation', { progress });
  }, [
    canEdit,
    updateAssessmentStage,
    assessmentStages?.observation?.status,
    confidenceTernary,
    criteriaEvidence,
  ]);

  // ── Review state persistence (single writer) ───────────────────────────
  const reviewHydratedRef = useRef(false);
  useEffect(() => {
    if (!reviewHydratedRef.current) { reviewHydratedRef.current = true; return; }
    if (!canEdit) return;
    updateAssessmentStage('observation', maybePromoteStatus({
      'review.confidence': confidence,
      'review.confidenceTernary': confidenceTernary,
      'review.formulationConditions': formulationConditions,
      'review.differentialsConfirmed': differentialsConfirmed,
      'review.emergingFormulation': emergingFormulation,
      'review.formulationNotes': confidentialNotes,
    }));
  }, [canEdit, updateAssessmentStage, maybePromoteStatus, confidence, confidenceTernary, formulationConditions, differentialsConfirmed, emergingFormulation, confidentialNotes]);

  /**
   * Recording the ternary confidence rating completes the Review third of the
   * progress fraction, but it happens on the Review page rather than through
   * the task-data handlers that normally write progress — so mirror it here.
   * Deps are deliberately narrow: reacting to allTaskData too would re-write an
   * identical fraction on every keystroke.
   */
  const progressReviewHydratedRef = useRef(false);
  useEffect(() => {
    if (!progressReviewHydratedRef.current) { progressReviewHydratedRef.current = true; return; }
    computeAndWriteProgress(allTaskData, selectedTaskIds, confidenceTernary);
  }, [confidenceTernary]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Review handlers ────────────────────────────────────────────────────
  const handleToggleCondition = useCallback((condition) => {
    setFormulationConditions(prev =>
      prev.includes(condition) ? prev.filter(c => c !== condition) : [...prev, condition]);
  }, []);

  const handleConfirmDifferentials = useCallback(() => setDifferentialsConfirmed(true), []);
  const handleChangeDifferentials = useCallback(() => setDifferentialsConfirmed(false), []);

  // ── ADOS handlers ─────────────────────────────────────────────────────
  const handleAdosFieldChange = useCallback((fieldKey, value) => {
    setAdosData(prev => ({ ...prev, [fieldKey]: value }));
    if (!canEdit) return;
    updateAssessmentStage('observation', maybePromoteStatus({
      [`data.adosData.${fieldKey}`]: value,
    }));
  }, [canEdit, updateAssessmentStage, maybePromoteStatus]);

  // ── Criteria-level evidence (Criterion Mapping redesign) ───────────────
  const handleCriteriaEvidenceChange = useCallback((criterionId, value) => {
    setCriteriaEvidence((prev) => {
      const next = { ...prev, [criterionId]: value };
      if (canEdit) {
        updateAssessmentStage('observation', maybePromoteStatus({
          [`data.criteriaEvidence.${criterionId}`]: value,
        }));
        // Progress depends on criteria completeness — recompute with the
        // freshest evidence map so Overview does not lag a full debounce.
        computeAndWriteProgress(allTaskData, selectedTaskIds, confidenceTernary, next);
      }
      return next;
    });
  }, [
    canEdit,
    updateAssessmentStage,
    maybePromoteStatus,
    computeAndWriteProgress,
    allTaskData,
    selectedTaskIds,
    confidenceTernary,
  ]);

  // Optional additional notes typed on a Criterion Mapping page (below the
  // two tagged-note panels). These supplement — not replace — upstream tags.
  const handleCriteriaNotesChange = useCallback((criterionId, notes) => {
    setCriteriaNotes((prev) => ({ ...prev, [criterionId]: notes }));
    if (!canEdit) return;
    updateAssessmentStage('observation', maybePromoteStatus({
      [`data.criteriaNotes.${criterionId}`]: notes,
    }));
  }, [canEdit, updateAssessmentStage, maybePromoteStatus]);

  /**
   * Re-tag a note that lives in Task Admin or ADOS storage from Criterion
   * Mapping. `sourceRef` comes from collectObsTaggedNotes and points at the
   * exact array that owns the note, so edits stay in sync when the clinician
   * later returns to those phases.
   *
   * @param {object} sourceRef
   * @param {'task'|'ados'} sourceRef.kind
   * @param {string|number} sourceRef.dataKey
   * @param {string} sourceRef.field
   * @param {string|number} noteId
   * @param {string[]} criteriaTags
   */
  const handleUpdateNoteCriteriaTags = useCallback((sourceRef, noteId, criteriaTags) => {
    if (!sourceRef || noteId == null) return;

    if (sourceRef.kind === 'ados') {
      const field = sourceRef.field || `ados_${sourceRef.dataKey}_notes`;
      setAdosData((prev) => {
        const existing = Array.isArray(prev[field]) ? prev[field] : [];
        const nextNotes = existing.map((n) => (
          n && n.id === noteId ? { ...n, criteriaTags } : n
        ));
        if (canEdit) {
          updateAssessmentStage('observation', maybePromoteStatus({
            [`data.adosData.${field}`]: nextNotes,
          }));
        }
        return { ...prev, [field]: nextNotes };
      });
      return;
    }

    if (sourceRef.kind === 'task') {
      const { dataKey, field } = sourceRef;
      if (dataKey == null || !field) return;
      setAllTaskData((prev) => {
        const entry = prev[dataKey] || {};
        const existing = Array.isArray(entry[field]) ? entry[field] : [];
        const nextNotes = existing.map((n) => (
          n && n.id === noteId ? { ...n, criteriaTags } : n
        ));
        const next = {
          ...prev,
          [dataKey]: { ...entry, [field]: nextNotes },
        };
        if (canEdit) {
          updateAssessmentStage('observation', maybePromoteStatus({
            [`data.allTaskData.${dataKey}.${field}`]: nextNotes,
          }));
        }
        return next;
      });
    }
  }, [canEdit, updateAssessmentStage, maybePromoteStatus]);

  // ── Task management ────────────────────────────────────────────────────
  const handleAddTask = useCallback((taskId) => {
    setSelectedTaskIds(prev => {
      if (prev.includes(taskId)) return prev;
      const next = [...prev, taskId];
      if (canEdit) {
        updateAssessmentStage('observation', maybePromoteStatus({
          'data.selectedTaskIds': next,
        }));
        computeAndWriteProgress(allTaskData, next);
      }
      return next;
    });
  }, [canEdit, updateAssessmentStage, maybePromoteStatus, computeAndWriteProgress, allTaskData]);

  const handleRemoveTask = useCallback((taskId) => {
    setSelectedTaskIds(prev => {
      const next = prev.filter(id => id !== taskId);
      if (canEdit) {
        updateAssessmentStage('observation', maybePromoteStatus({
          'data.selectedTaskIds': next,
        }));
        computeAndWriteProgress(allTaskData, next);
      }
      return next;
    });
  }, [canEdit, updateAssessmentStage, maybePromoteStatus, computeAndWriteProgress, allTaskData]);

  // ── Field change handlers (optimistic local + granular Firestore) ──────
  const handleFieldChange = useCallback((activeTaskId, fieldId, value) => {
    setAllTaskData(prev => {
      const updated = {
        ...prev,
        [activeTaskId]: {
          ...(prev[activeTaskId] || {}),
          [fieldId]: value,
        },
      };
      if (canEdit) {
        updateAssessmentStage('observation', maybePromoteStatus({
          [`data.allTaskData.${activeTaskId}.${fieldId}`]: value,
        }));
        computeAndWriteProgress(updated, selectedTaskIds);
      }
      return updated;
    });
  }, [canEdit, updateAssessmentStage, maybePromoteStatus, computeAndWriteProgress, selectedTaskIds]);

  const handleGeneralFieldChange = useCallback((panelId, fieldId, value) => {
    const key = `general_${panelId}`;
    setAllTaskData(prev => {
      const updated = {
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          [fieldId]: value,
        },
      };
      if (canEdit) {
        updateAssessmentStage('observation', maybePromoteStatus({
          [`data.allTaskData.${key}.${fieldId}`]: value,
        }));
      }
      return updated;
    });
  }, [canEdit, updateAssessmentStage, maybePromoteStatus]);

  // ── Cross-page note move ───────────────────────────────────────────────
  const noteMoveTargets = useMemo(
    () => buildObsNoteMoveTargets({ selectedTaskIds, tasks, allTaskData, excludeSensoryPanel }),
    [selectedTaskIds, tasks, allTaskData, excludeSensoryPanel],
  );

  const handleMoveNoteAcross = useCallback((sourceId, noteId, targetId, insertAt, notePatch) => {
    if (sourceId === targetId) return;
    const src = parseObsBoxId(sourceId);
    const tgt = parseObsBoxId(targetId);
    if (!src || !tgt) return;

    setAllTaskData(prev => {
      const sourceData = prev[src.dataKey] || prev[Number(src.dataKey)] || {};
      const targetData = prev[tgt.dataKey] || prev[Number(tgt.dataKey)] || {};
      const sourceNotes = Array.isArray(sourceData[src.field]) ? sourceData[src.field] : [];
      const targetNotes = Array.isArray(targetData[tgt.field]) ? targetData[tgt.field] : [];

      // v2: notes moved into the Self-Report box get the same auto-tag as notes
      // typed there directly, so they show the SR chip and stay in the
      // Self-Report LLM sweep without a second Tag click.
      let patch = notePatch || null;
      if (isObsV2 && tgt.dataKey === 'general_self_reported') {
        const live = sourceNotes.find((n) => n && n.id === noteId);
        const fromPatch = Array.isArray(patch?.criteriaTags) ? patch.criteriaTags : null;
        const tags = fromPatch
          || (Array.isArray(live?.criteriaTags) ? live.criteriaTags : []);
        if (!tags.includes(SELF_REPORTED_TAG_ID)) {
          patch = { ...(patch || {}), criteriaTags: [...tags, SELF_REPORTED_TAG_ID] };
        }
      }

      const result = moveNoteBetweenArrays(sourceNotes, targetNotes, noteId, insertAt, patch);
      if (!result) return prev;

      const dotPaths = {};
      const next = { ...prev };
      const applyField = (dataKey, field, value) => {
        next[dataKey] = { ...(next[dataKey] || {}), [field]: value };
        dotPaths[`data.allTaskData.${dataKey}.${field}`] = value;
      };

      applyField(src.dataKey, src.field, result.source);
      applyField(tgt.dataKey, tgt.field, result.target);

      // Strip orphaned mapped copies when moving a master note out of its task.
      if (src.kind === 'task' && src.field === 'masterNotes') {
        const taskObj = next[src.dataKey] || {};
        for (const key of Object.keys(taskObj)) {
          if (/^dsm_.*_notes$/.test(key) && Array.isArray(taskObj[key])
            && taskObj[key].some(n => n && n.id === noteId)) {
            applyField(src.dataKey, key, taskObj[key].filter(n => !n || n.id !== noteId));
          }
        }
      }

      if (canEdit) {
        updateAssessmentStage('observation', maybePromoteStatus(dotPaths));
        computeAndWriteProgress(next, selectedTaskIds);
      }
      return next;
    });
  }, [canEdit, updateAssessmentStage, maybePromoteStatus, computeAndWriteProgress, selectedTaskIds, isObsV2]);

  return {
    // IDs and config
    patientId,
    tasks,
    navigate,

    // Context refs
    header,
    assessmentStages,
    reportContent,
    flushStageWrites,

    // Permission flags
    isPreviewMode,
    canEdit,

    // Workflow version — v2 unlocks the tagged-notes / criteria-evidence
    // redesign (tag pickers, criteria-by-criteria mapping, Other Behaviours).
    // Absent stamp -> false, so v1 patients keep the original flow untouched.
    isObsV2,

    // Appointment checklist feature gate (stamp-resolved, not org-name based).
    isAppointmentChecklistEnabled,

    // Clinical data state
    selectedTaskIds,
    setSelectedTaskIds,
    allTaskData,
    setAllTaskData,
    confidenceStyle,
    adosData,
    criteriaEvidence,
    criteriaNotes,

    // Review state
    confidence,
    setConfidence,
    confidenceTernary,
    setConfidenceTernary,
    formulationConditions,
    differentialsConfirmed,
    emergingFormulation,
    setEmergingFormulation,
    confidentialNotes,
    setConfidentialNotes,

    // Review handlers
    handleToggleCondition,
    handleConfirmDifferentials,
    handleChangeDifferentials,

    // Task management handlers
    handleAddTask,
    handleRemoveTask,

    // Field change handlers (note: these take taskId/panelId as first arg now)
    handleFieldChange,
    handleGeneralFieldChange,

    // ADOS handler
    handleAdosFieldChange,

    // Criteria-level evidence + optional notes (Criterion Mapping redesign)
    handleCriteriaEvidenceChange,
    handleCriteriaNotesChange,
    handleUpdateNoteCriteriaTags,

    // Note move
    noteMoveTargets,
    handleMoveNoteAcross,

    // Status/progress helpers
    maybePromoteStatus,
    updateAssessmentStage,
    computeAndWriteProgress,
  };
}
